import { Palette, Check } from 'lucide-react';
import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

// Canva-inspired color palettes
export const THEME_PALETTES = [
  {
    id: 'midnight-blue',
    name: 'Midnight Blue',
    colors: {
      primary: '#1E3A5F',
      secondary: '#2E5077',
      accent: '#4A90D9',
      background: '#0D1B2A',
      text: '#E8F1F8'
    }
  },
  {
    id: 'coral-sunset',
    name: 'Coral Sunset',
    colors: {
      primary: '#FF6B6B',
      secondary: '#FFA07A',
      accent: '#FFE66D',
      background: '#2C1810',
      text: '#FFF5F0'
    }
  },
  {
    id: 'forest-green',
    name: 'Forest Green',
    colors: {
      primary: '#2D5A27',
      secondary: '#4A7C59',
      accent: '#8FBC8F',
      background: '#1A2E1A',
      text: '#E8F5E8'
    }
  },
  {
    id: 'royal-purple',
    name: 'Royal Purple',
    colors: {
      primary: '#6B4C9A',
      secondary: '#9B7ED9',
      accent: '#D4A5FF',
      background: '#1A1425',
      text: '#F5EFFF'
    }
  },
  {
    id: 'ocean-teal',
    name: 'Ocean Teal',
    colors: {
      primary: '#008B8B',
      secondary: '#20B2AA',
      accent: '#48D1CC',
      background: '#0A1F1F',
      text: '#E0FFFF'
    }
  },
  {
    id: 'warm-earth',
    name: 'Warm Earth',
    colors: {
      primary: '#8B4513',
      secondary: '#CD853F',
      accent: '#DEB887',
      background: '#1F1410',
      text: '#FFF8DC'
    }
  },
  {
    id: 'electric-pink',
    name: 'Electric Pink',
    colors: {
      primary: '#FF1493',
      secondary: '#FF69B4',
      accent: '#FFB6C1',
      background: '#1A0A12',
      text: '#FFF0F5'
    }
  },
  {
    id: 'slate-modern',
    name: 'Slate Modern',
    colors: {
      primary: '#475569',
      secondary: '#64748B',
      accent: '#94A3B8',
      background: '#0F172A',
      text: '#F1F5F9'
    }
  },
  {
    id: 'golden-hour',
    name: 'Golden Hour',
    colors: {
      primary: '#DAA520',
      secondary: '#F4C430',
      accent: '#FFD700',
      background: '#1A1508',
      text: '#FFFACD'
    }
  },
  {
    id: 'arctic-blue',
    name: 'Arctic Blue',
    colors: {
      primary: '#5DADE2',
      secondary: '#85C1E9',
      accent: '#AED6F1',
      background: '#0B1929',
      text: '#EBF5FB'
    }
  },
  {
    id: 'berry-wine',
    name: 'Berry Wine',
    colors: {
      primary: '#722F37',
      secondary: '#A0455A',
      accent: '#C97B84',
      background: '#1A0A0D',
      text: '#F8E8EB'
    }
  },
  {
    id: 'lime-fresh',
    name: 'Lime Fresh',
    colors: {
      primary: '#32CD32',
      secondary: '#7CFC00',
      accent: '#ADFF2F',
      background: '#0A1A0A',
      text: '#F0FFF0'
    }
  }
];

export type ThemePalette = typeof THEME_PALETTES[0];

interface ThemePaletteSelectorProps {
  onApply: (palette: ThemePalette) => void;
  disabled?: boolean;
  className?: string;
}

interface PopoverPosition {
  top: number;
  left: number;
}

export function ThemePaletteSelector({ onApply, disabled = false, className }: ThemePaletteSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [position, setPosition] = useState<PopoverPosition>({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Calculate position when opening
  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const popoverWidth = Math.min(520, window.innerWidth - 32);
    const popoverHeight = 180; // Approximate height

    // Position above the button
    let top = rect.top - popoverHeight - 8;
    let left = rect.left;

    // If would go off top of screen, position below instead
    if (top < 8) {
      top = rect.bottom + 8;
    }

    // Keep within horizontal bounds
    if (left + popoverWidth > window.innerWidth - 16) {
      left = window.innerWidth - popoverWidth - 16;
    }
    if (left < 16) {
      left = 16;
    }

    setPosition({ top, left });
  }, []);

  // Update position on open and resize
  useEffect(() => {
    if (!isOpen) return;

    updatePosition();

    const handleResize = () => updatePosition();
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, true);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize, true);
    };
  }, [isOpen, updatePosition]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        popoverRef.current &&
        !popoverRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    // Use setTimeout to avoid immediate close on the click that opened it
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleApply = (palette: ThemePalette) => {
    setSelectedId(palette.id);
    onApply(palette);
    setIsOpen(false);
    // Reset selection after a short delay
    setTimeout(() => setSelectedId(null), 300);
  };

  const handleToggle = () => {
    if (!isOpen) {
      updatePosition();
    }
    setIsOpen(!isOpen);
  };

  // Render popover using portal
  const popoverContent = isOpen ? createPortal(
    <div
      ref={popoverRef}
      className="fixed z-[9999] animate-in fade-in-0 slide-in-from-bottom-2 duration-200"
      style={{
        top: position.top,
        left: position.left,
        width: 'min(520px, calc(100vw - 32px))'
      }}
    >
      <div className="bg-popover/95 backdrop-blur-xl border border-border/50 rounded-xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-3 py-2 border-b border-border/50">
          <div className="flex items-center gap-2">
            <Palette className="h-3.5 w-3.5 text-primary" />
            <span className="text-xs font-medium">Choose Theme</span>
            <span className="text-xs text-muted-foreground ml-auto">← scroll →</span>
          </div>
        </div>

        {/* Horizontally scrolling palette cards */}
        <div
          className="flex gap-2 p-3 overflow-x-auto scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent"
          style={{ scrollBehavior: 'smooth' }}
        >
          {THEME_PALETTES.map((palette) => (
            <button
              key={palette.id}
              onClick={() => handleApply(palette)}
              className={cn(
                "flex-shrink-0 group relative p-2.5 rounded-lg border transition-all duration-200",
                "hover:border-primary/50 hover:bg-muted/50",
                selectedId === palette.id
                  ? "border-primary bg-primary/10"
                  : "border-border/50 bg-background/50"
              )}
              style={{ width: '110px' }}
            >
              {/* Color dots row */}
              <div className="flex gap-1 justify-center mb-2">
                {Object.values(palette.colors).slice(0, 4).map((color, i) => (
                  <div
                    key={i}
                    className="w-5 h-5 rounded-full border border-white/20 shadow-sm ring-1 ring-black/10"
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>

              {/* Theme name */}
              <p className="text-[11px] font-medium text-center truncate text-foreground/90">
                {palette.name}
              </p>

              {/* Selected indicator */}
              {selectedId === palette.id && (
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-primary rounded-full flex items-center justify-center">
                  <Check className="h-2.5 w-2.5 text-primary-foreground" />
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Footer hint */}
        <div className="px-3 py-1.5 border-t border-border/50 bg-muted/30">
          <p className="text-[10px] text-muted-foreground text-center">
            Click a theme to apply it to your slides
          </p>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div className={cn("relative", className)}>
      <Button
        ref={buttonRef}
        variant="ghost"
        size="icon"
        disabled={disabled}
        onClick={handleToggle}
        className={cn(
          "h-8 w-8 rounded-full transition-all hover:bg-muted/50",
          isOpen && "bg-muted/50"
        )}
        aria-label="Choose color theme"
        title="Apply color theme"
      >
        <Palette className="h-4 w-4" />
      </Button>

      {popoverContent}
    </div>
  );
}
